import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import * as XLSX from 'xlsx';
// import { useCSVReader } from 'react-papaparse';
import { CalendarIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { FaPlane } from 'react-icons/fa6';
import { FaCalendarAlt } from 'react-icons/fa';
import Layout from '@/Components/layout/Layout';

// Setup the localizer by providing the moment Object
const localizer = momentLocalizer(moment);

// Replace with your actual Google Sheets URL
// const DEFAULT_GOOGLE_SHEETS_URL = 'https://docs.google.com/spreadsheets/d/19hewxuFRpX-eYMKn8KPmnNAvygLmkKm33b9uUG11xdo/edit?usp=sharing';

const DEFAULT_GOOGLE_SHEETS_URL = 'https://docs.google.com/spreadsheets/d/1sUt-8jKwDRJ8Y3HokLod-y_nbe6zvWY_VkH6dS5oTEQ/edit?usp=sharing';

const FlightPlanViewer = () => {
    const [flightData, setFlightData] = useState([]);
    const [events, setEvents] = useState([]);
    // const [fileName, setFileName] = useState('Google Sheets Data');
    const [error, setError] = useState(null);
    const [view, setView] = useState('month');
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    // const { CSVReader } = useCSVReader();

    // Convert Excel serial date to JS Date (without timezone adjustment)
    const parseExcelDate = (excelDate) => {
        const utcDays = Math.floor(excelDate - 25569);
        const utcValue = utcDays * 86400;
        return new Date(utcValue * 1000);
    };

    const processFile = useCallback((data, fileInfo) => {
        try {
            // setFileName(fileInfo.name);
            setError(null);

            const transformedData = data.map((row, index) => {
                if (index === 0 && (row[0] === 'FROM' || row[0] === 'FROM\tFROM')) return null;
                if (!row[0]) return null;

                let dateObj = new Date();
                let originalDateString = '';
                const dateValue = row[8];

                if (dateValue) {
                    if (typeof dateValue === 'number') {
                        dateObj = parseExcelDate(dateValue);
                        dateObj.setDate(dateObj.getDate() + 1);
                        originalDateString = moment(dateObj).format('dddd, MMMM D, YYYY');
                    } else if (typeof dateValue === 'string') {
                        originalDateString = dateValue;
                        const m = moment(dateValue, 'dddd, MMMM D, YYYY');
                        dateObj = m.isValid() ? m.toDate() : new Date();
                        dateObj.setDate(dateObj.getDate() + 1);
                    } else if (dateValue instanceof Date) {
                        dateObj = new Date(dateValue);
                        dateObj.setDate(dateObj.getDate() + 1);
                        originalDateString = moment(dateObj).format('dddd, MMMM D, YYYY');
                    }
                }

                let timeHours = 0, timeMinutes = 0;
                if (row[5]) {
                    const timeStr = row[5].toString();
                    if (timeStr.includes(':')) {
                        [timeHours, timeMinutes] = timeStr.split(':').map(Number);
                    } else {
                        const decimalTime = parseFloat(timeStr);
                        timeHours = Math.floor(decimalTime);
                        timeMinutes = Math.round((decimalTime % 1) * 60);
                    }
                }

                return {
                    id: index,
                    fromCode: row[0] || '',
                    fromName: row[1] || '',
                    toCode: row[2] || '',
                    toName: row[3] || '',
                    miles: parseInt(row[4]) || 0,
                    timeHours: timeHours + (timeMinutes / 60),
                    timeMinutes: timeMinutes,
                    fuel: parseInt(row[7]) || 0,
                    date: dateObj,
                    originalDateString: originalDateString || moment(dateObj).format('dddd, MMMM D, YYYY')
                };
            }).filter(Boolean);

            setFlightData(transformedData);

            const calendarEvents = transformedData.map(flight => {
                const eventDate = new Date(flight.date);
                return {
                    id: flight.id,
                    title: `${flight.fromCode} → ${flight.toCode}`,
                    start: new Date(Date.UTC(
                        eventDate.getFullYear(),
                        eventDate.getMonth(),
                        eventDate.getDate()
                    )),
                    end: new Date(Date.UTC(
                        eventDate.getFullYear(),
                        eventDate.getMonth(),
                        eventDate.getDate()
                    )),
                    allDay: true,
                    resource: flight,
                };
            });

            setEvents(calendarEvents);
        } catch (err) {
            setError(`Error processing file: ${err.message}`);
            console.error('Processing error:', err);
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const exportUrl = DEFAULT_GOOGLE_SHEETS_URL
                    .replace(/\/edit.*$/, '')
                    .replace(/\/pub$/, '') + '/export?format=xlsx';

                const response = await fetch(exportUrl);
                if (!response.ok) {
                    throw new Error('Failed to fetch Google Sheets data');
                }

                const arrayBuffer = await response.arrayBuffer();
                const data = new Uint8Array(arrayBuffer);

                const workbook = XLSX.read(data, {
                    type: 'array',
                    cellDates: true,
                    cellNF: false,
                    cellText: false
                });

                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
                    header: 1,
                    defval: null,
                    dateNF: 'dddd, mmmm d, yyyy'
                });

                processFile(jsonData, { name: 'Google Sheets Data' });
            } catch (err) {
                setError(`Error loading Google Sheets: ${err.message}`);
                console.error('Google Sheets error:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();

        // Optional: Refresh data every 5 minutes
        const interval = setInterval(loadData, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [processFile]);

    // const handleClear = () => {
    //     setFlightData([]);
    //     setEvents([]);
    //     setFileName('');
    //     setError(null);
    //     setSearchTerm('');
    // };

    const filteredFlights = useMemo(() => {
        if (!searchTerm) return flightData;
        const term = searchTerm.toLowerCase();
        return flightData.filter(flight =>
            flight.fromCode.toLowerCase().includes(term) ||
            flight.fromName.toLowerCase().includes(term) ||
            flight.toCode.toLowerCase().includes(term) ||
            flight.toName.toLowerCase().includes(term) ||
            flight.originalDateString.toLowerCase().includes(term)
        );
    }, [flightData, searchTerm]);

    const filteredEvents = useMemo(() => {
        if (!searchTerm) return events;
        const term = searchTerm.toLowerCase();
        return events.filter(event =>
            event.resource.fromCode.toLowerCase().includes(term) ||
            event.resource.fromName.toLowerCase().includes(term) ||
            event.resource.toCode.toLowerCase().includes(term) ||
            event.resource.toName.toLowerCase().includes(term) ||
            moment(event.start).format('dddd, MMMM D, YYYY').toLowerCase().includes(term)
        );
    }, [events, searchTerm]);

    // const EventComponent = ({ event }) => {

    //     console.log(event)

    //     return (
    //         <div className="p-2 text-xs bg-white border border-blue-200 rounded shadow">
    //             <strong className="block text-sm font-medium">{event.title}</strong>
    //             <div className="mt-1 text-dark">
    //                 <div><span className="font-semibold">Title: {event.resource.fromName}</span> </div>
    //                 <div><span className="font-semibold">From: {event.resource.fromName}</span> </div>
    //                 <div><span className="font-semibold">To: {event.resource.toName}</span> </div>
    //                 <div><span className="font-semibold">Date:  {moment(event.start).format('dddd, MMMM D, YYYY')}</span></div>
    //             </div>
    //         </div>
    //     )
    // };

    const eventStyleGetter = (event) => {
        let backgroundColor = "#3182ce"; // Default blue for flights
        let borderColor = "#3182ce";

        if (event.resource?.type === "host-visit") {
            backgroundColor = "#38a169"; // Green for host visits
            borderColor = "#38a169";
        } else if (event.resource?.type === "event") {
            backgroundColor = "#9f7aea"; // Purple for special events
            borderColor = "#9f7aea";
        }

        return {
            style: {
                backgroundColor,
                borderRadius: "4px",
                opacity: 0.8,
                color: "white",
                border: `2px solid ${borderColor}`,
                display: "block",
            },
        };
    };

    return (
        <Layout>
            <div className="container p-4 mx-auto">
                <header className="bg-[#af2322] text-white py-6 shadow-lg my-4">
                    <div className="container px-4 mx-auto">
                        <h1 className="flex items-center text-3xl font-bold text-white">
                            <FaPlane className="mr-3" /> Fly-Inn Flight Plan
                        </h1>
                        <p className="flex items-center mt-2 text-white">
                            <FaCalendarAlt className="mr-2" /> April - July 2025 Tour Schedule
                        </p>
                    </div>
                </header>

                {isLoading && (
                    <div className="p-4 mb-6 text-center bg-white rounded-lg shadow">
                        <div className="flex justify-center">
                            <div className="w-8 h-8 border-4 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
                        </div>
                        <p className="mt-2 text-gray-600">Loading flight data...</p>
                    </div>
                )}

                {error && !isLoading && (
                    <div className="p-4 mb-6 text-center bg-red-100 rounded-lg shadow">
                        <p className="text-red-600">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 mt-2 text-sm text-white bg-red-600 rounded hover:bg-red-700"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {!isLoading && !error && flightData.length > 0 && (
                    <div className="relative mb-6">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            className="w-full p-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Search flights by airport, name, or date..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                )}

                {!isLoading && !error && (
                    <>
                        {flightData.length > 0 ? (
                            <div className="grid grid-cols-1 gap-6">
                                <div className="p-4 bg-white rounded-lg shadow lg:col-span-2">
                                    <div className="flex items-center justify-start mb-4">
                                        <h2 className="text-lg font-semibold">Flight Schedule</h2>
                                        {/* <div className="flex space-x-2">
                                        <button
                                            onClick={() => setView('month')}
                                            className={`px-3 py-1 text-sm rounded ${view === 'month' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                                        >
                                            Month
                                        </button>
                                        <button
                                            onClick={() => setView('week')}
                                            className={`px-3 py-1 text-sm rounded ${view === 'week' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                                        >
                                            Week
                                        </button>
                                        <button
                                            onClick={() => setView('day')}
                                            className={`px-3 py-1 text-sm rounded ${view === 'day' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                                        >
                                            Day
                                        </button>
                                    </div> */}
                                    </div>

                                    <div className="h-[600px]">
                                        <Calendar
                                            localizer={localizer}
                                            events={filteredEvents}
                                            startAccessor="start"
                                            endAccessor="end"
                                            views={['month', 'week', 'day']}
                                            defaultView="month"
                                            view={view}
                                            onView={setView}
                                            components={{
                                                event: ({ event }) => (
                                                    <div className="p-1 text-xs">
                                                        <FaPlane className="hidden mr-2 md:inline" />
                                                        <strong className="whitespace-break-spaces">
                                                            {event.title}
                                                        </strong>
                                                        <br />
                                                        <strong className="hidden whitespace-break-spaces md:inline">
                                                            {event.resource.fromName} →  {event.resource.toName}
                                                        </strong>
                                                        {event.resource?.details?.location && (
                                                            <div className="mt-1 text-xs">
                                                                {event.resource.details.location}
                                                            </div>
                                                        )}
                                                    </div>
                                                ),
                                            }}
                                            eventPropGetter={eventStyleGetter}
                                        />
                                    </div>
                                </div>

                                <div className="p-4 overflow-hidden bg-white rounded-lg shadow">
                                    <h2 className="mb-4 text-lg font-semibold">Flight Details</h2>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Date</th>
                                                    <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">From → To</th>
                                                    <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Miles</th>
                                                    <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Time</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {filteredFlights.map((flight) => (
                                                    <tr key={flight.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                                                            {flight.originalDateString}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                                                            <div>{flight.fromCode} → {flight.toCode}</div>
                                                            <div className="text-xs text-gray-500">{flight.fromName} to {flight.toName}</div>
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                                                            {flight.miles}
                                                        </td>
                                                        {/* <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                                                        {Math.floor(flight.timeHours)}h {flight.timeMinutes}m
                                                    </td> */}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="p-8 text-center bg-white rounded-lg shadow">
                                <CalendarIcon className="w-12 h-12 mx-auto text-gray-400" />
                                <p className="mt-2 text-gray-500">No flight data available</p>
                            </div>
                        )}

                        {flightData.length > 0 && (
                            <div className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-4">
                                <div className="p-4 bg-white rounded-lg shadow">
                                    <h3 className="text-sm font-medium text-gray-500">Total Flights</h3>
                                    <p className="mt-1 text-2xl font-semibold text-gray-900">{flightData.length}</p>
                                </div>
                                <div className="p-4 bg-white rounded-lg shadow">
                                    <h3 className="text-sm font-medium text-gray-500">Total Miles</h3>
                                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                                        {flightData.reduce((sum, flight) => sum + flight.miles, 0).toLocaleString()}
                                    </p>
                                </div>
                                {/* <div className="p-4 bg-white rounded-lg shadow">
                                <h3 className="text-sm font-medium text-gray-500">Total Flight Hours</h3>
                                <p className="mt-1 text-2xl font-semibold text-gray-900">
                                    {flightData.reduce((sum, flight) => sum + flight.timeHours, 0).toFixed(1)} hours
                                </p>
                            </div> */}
                                <div className="p-4 bg-white rounded-lg shadow">
                                    <h3 className="text-sm font-medium text-gray-500">Total Fuel</h3>
                                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                                        {flightData.reduce((sum, flight) => sum + flight.fuel, 0).toLocaleString()} gal
                                    </p>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <footer className="py-6 mt-8 bg-gray-100 border-t">
                    <div className="container px-4 mx-auto text-center text-gray-600">
                        <p>Fly-Inn All-inclusive, aviation-centric lodging network</p>
                        <p className="mt-1">
                            Follow the 2025 tour updates on our social media channels
                        </p>
                    </div>
                </footer>
            </div>
        </Layout>
    );
};

export default FlightPlanViewer;